import type * as React from "react";
import {
  Box,
  cn,
  Icon,
  selectTriggerIconClassName,
  selectTriggerVariants,
  Text,
  type IconName,
  type Omittable,
} from "@salestrak/ui";

/**
 * The Select field's dress on a popover trigger — what a vocabulary picker
 * (Reason, Contact method, Outcome) wears when it stands in a form as a
 * full-width field. Not a Select: the list underneath is the creatable
 * picker, but the control has to read as the form input beside the fields
 * around it. `outline` + a single chevron: the bordered box marks a field
 * still asking, and the placeholder keeps the field's question.
 *
 * Handed to the picker as its trigger, so the popover's own props (the
 * click, the `aria-expanded`, the anchor ref) arrive as props here and MUST
 * reach the button — a wrapper that kept them would be a trigger that opens
 * nothing.
 *
 * (className: the app-side spelling of a select-shaped popover trigger — the
 * theme-color-field composition; a recorded gap for the sweep.)
 */
export function VocabularyFieldTrigger({
  value,
  placeholder,
  ariaLabel,
  chevron = "chevron-down",
  className,
  ...props
}: Omittable<{
  /** The picked word; empty shows the placeholder in the muted rung. */
  value: string;
  placeholder: string;
  ariaLabel: string;
  /** `chevrons-up-down` for a searchable list that reads as a combobox. */
  chevron?: IconName;
}> &
  Omit<React.ComponentProps<"button">, "children" | "value">) {
  return (
    <Box
      as="button"
      type="button"
      aria-label={ariaLabel}
      {...props}
      className={cn(
        selectTriggerVariants({ variant: "outline", width: "full" }),
        className,
      )}
    >
      <Text
        size="sm"
        truncate
        variant={value ? undefined : "muted"}
        className="flex-1"
      >
        {value || placeholder}
      </Text>
      <Icon className={selectTriggerIconClassName} name={chevron} />
    </Box>
  );
}
